/**
 * Windows Desktop Launcher Component
 * Allows users to launch new Windows EC2 desktops with NICE DCV
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Card, Button, Select, Typography, Space, message, Spin, Radio, InputNumber } from "antd";
import { WindowsOutlined, RocketOutlined, DesktopOutlined, DatabaseOutlined, SafetyOutlined } from "@ant-design/icons";
import { launchWindowsDesktop } from "./store/windowsSlice";
import { getAvailableInstanceTypes } from "./api/windowsApi";
import axiosInstance from "../../utils/axios";
import "./WindowsDesktopLauncher.scss";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const WINDOWS_MIN_VOLUME_GB = 50;

// A single EBS volume cannot exceed 16 TiB, whatever the organisation's quota
// says — including an unlimited plan, whose quota is reported as a sentinel.
const EBS_MAX_VOLUME_GB = 16384;

function WindowsDesktopLauncher() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [instanceType, setInstanceType] = useState(null);
  const [persistent, setPersistent] = useState(true);
  const [isLaunching, setIsLaunching] = useState(false);
  const [instanceTypes, setInstanceTypes] = useState([]);
  const [instanceTypesLoading, setInstanceTypesLoading] = useState(true);
  const [storageGB, setStorageGB] = useState(WINDOWS_MIN_VOLUME_GB);
  const [maxStorageGB, setMaxStorageGB] = useState(1000);
  const [storageUnlimited, setStorageUnlimited] = useState(false);
  const [networkPolicyRef, setNetworkPolicyRef] = useState(null);
  const [networkPolicies, setNetworkPolicies] = useState([]);

  // Load allowed instance types from the capacity API.
  useEffect(() => {
    getAvailableInstanceTypes()
      .then((types) => {
        setInstanceTypes(types);
        // Pre-select the first available type.
        const first = types.find((t) => t.available);
        if (first) setInstanceType(first.type);
      })
      .catch(() => {})
      .finally(() => setInstanceTypesLoading(false));

    axiosInstance
      .get("capacity/storage")
      .then((res) => {
        const { availableGB, unlimited } = res.data || {};
        setStorageUnlimited(Boolean(unlimited));
        // An unlimited plan reports a sentinel, not a quantity, so the ceiling
        // falls back to what a single EBS volume can actually be.
        const cap = unlimited ? EBS_MAX_VOLUME_GB : Math.floor(availableGB || 0);
        if (cap > WINDOWS_MIN_VOLUME_GB) setMaxStorageGB(Math.min(cap, EBS_MAX_VOLUME_GB));
      })
      .catch(() => {});

    axiosInstance
      .get("/network-policies?page=1&limit=100")
      .then((res) => {
        const items = Array.isArray(res.data) ? res.data : [];
        setNetworkPolicies(items.filter((p) => p.scope === "vms" || p.scope === "all"));
      })
      .catch(() => {});
  }, []);

  const handleLaunch = async () => {
    setIsLaunching(true);

    try {
      const params = {
        template: "windows-dcv",
        instanceType: instanceType,
        volumeSize: storageGB,
        persistent: persistent
      };
      if (networkPolicyRef) {
        params.networkPolicyRef = networkPolicyRef;
      }
      const result = await dispatch(launchWindowsDesktop(params)).unwrap();

      message.success("Windows desktop is being provisioned...");

      // Navigate to the viewer
      navigate(`/control/windows/${result.id}`);
    } catch (error) {
      console.error("Failed to create Windows desktop:", error);
      message.error(error?.message || "Failed to launch Windows desktop");
      setIsLaunching(false);
    }
  };


  // The summary panel and the description must describe the profile the user
  // actually selected. They were hardcoded to the GPU profile, which was
  // accidentally correct while the catalog offered nothing else.
  const selectedSpec = instanceTypes.find((t) => t.type === instanceType) || null;
  const hasGPU = (selectedSpec?.gpu ?? 0) > 0;
  const gpuLabel = !selectedSpec
    ? "—"
    : hasGPU
      ? `${selectedSpec.gpu > 1 ? `${selectedSpec.gpu} × ` : ""}NVIDIA T4`
      : "None — CPU rendering";

  return (
    <div className="windows-launcher-container">
      <Card className="launcher-card">
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {/* Header */}
          <div className="launcher-header">
            <WindowsOutlined className="windows-icon" />
            <div>
              <Title level={3} style={{ margin: 0 }}>
                Windows Desktop
              </Title>
              <Text type="secondary">Launch a Windows EC2 instance with NICE DCV</Text>
            </div>
          </div>

          {/* Description */}
          <Paragraph>
            Launch a Windows desktop in the cloud
            {selectedSpec ? (hasGPU ? " with GPU acceleration" : ", CPU-rendered") : ""}. Your desktop will be
            accessible through NICE DCV for high-performance remote access.
          </Paragraph>

          {/* Instance Type Selection */}
          <div className="instance-selection">
            <Text strong>Instance Type</Text>
            <Select
              value={instanceType}
              onChange={setInstanceType}
              style={{ width: "100%" }}
              size="large"
              loading={instanceTypesLoading}
              disabled={isLaunching || instanceTypesLoading}
              placeholder="Select an instance type"
            >
              {instanceTypes.map((t) => (
                <Option key={t.type} value={t.type} disabled={!t.available}>
                  <Space style={{ opacity: t.available ? 1 : 0.45 }}>
                    <DesktopOutlined />
                    <span>
                      {t.type}
                      {" — "}
                      {t.family},{" "}
                      {t.vcpu} vCPU, {t.ram} GiB RAM
                      {t.gpu > 0 ? `, ${t.gpu} GPU` : ""}
                      {!t.available && (
                        <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                          ({t.unavailableReason})
                        </Text>
                      )}
                    </span>
                  </Space>
                </Option>
              ))}
            </Select>
          </div>

          {/* Desktop Mode */}
          <div className="mode-selection">
            <Text strong>Desktop Mode</Text>
            <Radio.Group
              value={persistent}
              onChange={(e) => setPersistent(e.target.value)}
              disabled={isLaunching}
              style={{ width: "100%", marginTop: 8 }}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value={true} style={{ width: "50%", textAlign: "center" }}>
                Persistent
              </Radio.Button>
              <Radio.Button value={false} style={{ width: "50%", textAlign: "center" }}>
                Ephemeral
              </Radio.Button>
            </Radio.Group>
            <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
              {persistent
                ? "You can pause your desktop and resume later. Storage is preserved."
                : "Desktop runs until you exit. Termination destroys all data."}
            </Text>
          </div>

          {/* Network Policy */}
          <div className="instance-selection">
            <Space>
              <SafetyOutlined />
              <Text strong>Traffic Filtering Policy</Text>
            </Space>
            <Select
              value={networkPolicyRef}
              onChange={setNetworkPolicyRef}
              style={{ width: "100%", marginTop: 8 }}
              size="large"
              disabled={isLaunching}
              allowClear
              placeholder="None (unrestricted egress)"
            >
              {networkPolicies.map((policy) => (
                <Option key={policy.name} value={policy.name}>
                  {policy.name}
                </Option>
              ))}
            </Select>
            <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
              {networkPolicyRef
                ? "Egress filtered via Squid proxy — only allowed hosts will be reachable."
                : "No policy — VM has unrestricted internet access."}
            </Text>
          </div>

          {/* Storage */}
          <div className="instance-selection">
            <Space>
              <DatabaseOutlined />
              <Text strong>Storage</Text>
            </Space>
            <div style={{ marginTop: 8 }}>
              <InputNumber
                min={WINDOWS_MIN_VOLUME_GB}
                max={maxStorageGB}
                step={10}
                value={storageGB}
                onChange={(v) => setStorageGB(v ?? WINDOWS_MIN_VOLUME_GB)}
                addonAfter="GiB"
                style={{ width: 180 }}
                disabled={isLaunching}
              />
              <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                min {WINDOWS_MIN_VOLUME_GB} GiB · max {maxStorageGB} GiB per desktop
                {storageUnlimited ? " · org quota: unlimited" : " available"}
              </Text>
            </div>
          </div>

          {/* Features */}
          <div className="features-grid">
            <div className="feature-item">
              <Text strong>GPU</Text>
              <Text type="secondary">{gpuLabel}</Text>
            </div>
            <div className="feature-item">
              <Text strong>Remote Access</Text>
              <Text type="secondary">NICE DCV</Text>
            </div>
            <div className="feature-item">
              <Text strong>OS</Text>
              <Text type="secondary">Windows Server 2022</Text>
            </div>
          </div>

          {/* Launch Button */}
          <Button
            type="primary"
            size="large"
            icon={isLaunching ? <Spin size="small" /> : <RocketOutlined />}
            onClick={handleLaunch}
            disabled={isLaunching || !instanceType || !storageGB || storageGB < WINDOWS_MIN_VOLUME_GB}
            block
          >
            {isLaunching ? "Launching..." : "Launch Windows Desktop"}
          </Button>

          {isLaunching && (
            <Text type="secondary" style={{ textAlign: "center", display: "block" }}>
              This may take 2-3 minutes. You will be redirected automatically.
            </Text>
          )}
        </Space>
      </Card>
    </div>
  );
}

export default WindowsDesktopLauncher;
